import { useEffect, useState } from "react";
import type { ProcedureCode, ToothDetail } from "@dentacore/shared-types";

import {
  cancelToothProcedure,
  completeToothProcedure,
  getTooth,
  listProcedureCodes,
  markToothNormal,
  markToothRemoved,
  recordToothProcedure,
} from "../api/patients";
import { Pager, useFitPages } from "../ui/paging";
import { Select } from "../ui/select";
import styles from "./tooth-editor.module.css";
import { isPrimaryTooth } from "./tooth-geometry";
import { SURFACE_ORDER, SurfaceSquare, surfaceLetter, surfaceName } from "./tooth-surfaces";
import type { Surface } from "./tooth-surfaces";
import { Link } from "react-router-dom";
import { useBranchTimezone } from "../api/use-branch-timezone";
import { useCan } from "../auth/use-can";
import { whenOf } from "../plans/standing";
import { useRingAsk } from "./ring-ask";
import type { RingAsk } from "./ring-ask";

/**
 * What the quick ring can ask this panel to do (STORY-221) — each one a button
 * already here: *To be done* and *Done now* set what the next procedure is
 * recorded as, *Mark done* is a planned row's ✓, and *Mark removed* and
 * *Return to normal* are the two whole-tooth actions at the foot.
 */
export type ToothAskKind = "planned" | "done" | "markDone" | "removed" | "normal";
export interface ToothAsk extends RingAsk<ToothAskKind> {
  /** For `markDone`: the planned procedure whose ✓ is pressed. */
  procedureId?: string;
}

/** How each state reads in the panel's heading. */
const STATE_LABEL: Record<string, string> = {
  SOUND: "Not worked on",
  TREATED: "Worked on",
  PLANNED: "Planned",
  MISSING: "Removed",
};

/**
 * The one row in the procedure list that is not a procedure: it opens the
 * free-text field. A sentinel rather than an empty string, so it can never
 * collide with a catalogue id.
 */
const OTHER = "__other__";

/**
 * What a list row and a baby-tooth box are expected to measure before they
 * have been drawn — the pager replaces each with the real height (PF13).
 */
const ROW_ESTIMATE = 26;
const BOX_ESTIMATE = 110;

export interface ToothEditorProps {
  patientId: string;
  fdiNumber: number;
  /** Called whenever the tooth changes, so the chart beside it can redraw. */
  onChanged: (detail: ToothDetail) => void;
  /** Puts a close button in the heading — the band under the chart (PF7). */
  onClose?: () => void;
  /**
   * Three columns across the band under the chart (PF7) — what is on the
   * tooth, what is planned and booked for it, and the two whole-tooth actions —
   * rather than one tall column beside it.
   */
  wide?: boolean;
  /**
   * In the column beside the chart on a short window (PF14), rather than the
   * band under it: the heading and the actions across, the two lists side by
   * side between them. Only with `wide`.
   */
  beside?: boolean;
  /**
   * Where this tooth stands among the baby teeth (BT10), worked out by the
   * patient file from the chart, which is the only thing that knows. Absent
   * while the chart draws no baby teeth.
   */
  primary?: PrimaryPlace;
  /** The quick ring asking for one of this panel's own buttons (STORY-221). */
  ask?: ToothAsk | null;
  /** Told once the ask has been honoured, so it is not honoured again. */
  onAsked?: () => void;
}

export interface PrimaryPlace {
  /** An adult tooth still behind a baby tooth: that baby tooth's number. */
  waitingBehind: number | null;
  /** An adult tooth standing in a fallen baby tooth's place: that baby tooth's number. */
  replaced: number | null;
  /** Mark the baby tooth in front of this adult tooth fallen, so the adult tooth takes its place. */
  onReplace: (adultFdi: number) => void;
  /** Put that baby tooth back, and this adult tooth back behind it. */
  onPutBack: (adultFdi: number) => void;
}

/**
 * The panel beside the chart: what is on this tooth, and how to change it.
 *
 * **Nothing here decides a tooth's state.** Every control posts an intent —
 * record this, cancel that, mark removed, return to normal — and renders
 * whatever the server derives. A client that computed the state itself would be
 * a second implementation of the rule, and the two would disagree the first
 * time one was changed.
 *
 * ── Rebuilt 2026-09-07, from the client's own reading of it ─────────────────
 *
 * *"I find it a lot crowded, too many dropdowns and fields that are left blank
 * … if I record a procedure I should not have to type what was done, so why
 * would it show … the recorded procedures show one after the other in a record
 * section above the dropdown meaning I can infinitely add stuff."*
 *
 * Three things were true and each made the others worse:
 *
 * 1. **Two adders.** A catalogue `<select>` and an always-visible free-text box
 *    under it, so every tooth opened with a blank field nobody had asked for.
 *    They are one searchable list now, with typing as a row inside it.
 * 2. **The same rows listed twice.** "Recorded" showed the live procedures and
 *    "History" showed all of them, so a tooth with three procedures printed six
 *    lines. One dated list now.
 * 3. **The list grew upward, above the control feeding it.** The adder sits
 *    under the list, so recording appends to what you are reading.
 */
export function ToothEditor({
  patientId,
  fdiNumber,
  onChanged,
  onClose,
  wide = false,
  beside = false,
  primary,
  ask,
  onAsked,
}: ToothEditorProps): React.JSX.Element {
  const editorClass = [styles.editor, wide && styles.wide, wide && beside && styles.beside]
    .filter(Boolean)
    .join(" ");
  const timezone = useBranchTimezone();
  /** Charting is `clinical.chart.edit` (QA third run); without it this only reads. */
  const charts = useCan()("clinical.chart.edit");
  const [detail, setDetail] = useState<ToothDetail | null>(null);
  /** The baby tooth this adult tooth replaced, for what was recorded on it (BT10). */
  const [babyDetail, setBabyDetail] = useState<ToothDetail | null>(null);
  const replaced = primary?.replaced ?? null;
  const [catalogue, setCatalogue] = useState<ProcedureCode[]>([]);
  const [label, setLabel] = useState("");
  /** Whether the free-text field is showing, which only an explicit choice does. */
  const [typing, setTyping] = useState(false);
  /**
   * **The parts of the tooth the next procedure touches** — optional. None
   * chosen records the work on the whole tooth, as it always did.
   */
  const [chosen, setChosen] = useState<Surface[]>([]);
  /**
   * **Done now, or to be done** — what the next procedure is recorded as.
   * Done is the default, as charting always was; *To be done* records planned
   * work, red on the chart until someone marks it done.
   */
  const [plannedNext, setPlannedNext] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();

    setDetail(null);
    setError(null);
    setLabel("");
    // A different tooth is a fresh start: an open text field carried across
    // would be aimed at the tooth you just left. So would chosen parts.
    setTyping(false);
    setChosen([]);
    setPlannedNext(false);

    getTooth(patientId, fdiNumber, controller.signal)
      .then(setDetail)
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return;
        setError(cause instanceof Error ? cause.message : "Could not open this tooth.");
      });

    return () => controller.abort();
  }, [patientId, fdiNumber]);

  // The replaced baby tooth is off the chart, so its record is read here: what
  // was done on it before it fell still belongs to this place in the mouth.
  useEffect(() => {
    setBabyDetail(null);
    if (replaced === null) return;
    const controller = new AbortController();
    getTooth(patientId, replaced, controller.signal)
      .then(setBabyDetail)
      .catch(() => setBabyDetail(null));
    return () => controller.abort();
  }, [patientId, replaced]);

  useEffect(() => {
    const controller = new AbortController();

    // The catalogue is per branch and ships empty, so a failure here is not an
    // error worth showing — it just means charting is free text, which it is
    // allowed to be.
    listProcedureCodes(controller.signal)
      .then(setCatalogue)
      .catch(() => setCatalogue([]));

    return () => controller.abort();
  }, []);

  /** Every mutation goes through here, so success and failure look the same. */
  const apply = (work: () => Promise<ToothDetail>): void => {
    setBusy(true);
    setError(null);

    work()
      .then((next) => {
        setDetail(next);
        onChanged(next);
        setLabel("");
        setTyping(false);
        setChosen([]);
        setPlannedNext(false);
      })
      .catch((cause: unknown) => {
        setError(cause instanceof Error ? cause.message : "That change did not save.");
      })
      .finally(() => setBusy(false));
  };

  /** A planned row's ✓. */
  const markDone = (procedureId: string): void =>
    apply(() => completeToothProcedure(patientId, fdiNumber, procedureId));

  /** *Mark removed* — or *Mark fallen* on a baby tooth. */
  const markRemoved = (): void => {
    // A baby tooth that falls is replaced (BT10): the patient file marks
    // it, moves the selection to the adult tooth and offers Undo.
    if (isPrimaryTooth(fdiNumber) && primary !== undefined) primary.onReplace(fdiNumber - 40);
    else apply(() => markToothRemoved(patientId, fdiNumber));
  };

  /** *Return to normal*, which undoes Mark removed. */
  const markNormal = (): void => apply(() => markToothNormal(patientId, fdiNumber));

  /**
   * **The quick ring's tooth actions** (STORY-221), each run only where its
   * button would be pressable — so a ring opened a moment before the tooth
   * changed cannot do what the panel would now refuse.
   */
  useRingAsk(
    ask,
    detail !== null && detail.fdiNumber === fdiNumber,
    (asked) => {
      if (!charts || busy || detail === null) return;
      const waiting = (primary?.waitingBehind ?? null) !== null;
      switch (asked.kind) {
        case "planned":
          if (!waiting) setPlannedNext(true);
          return;
        case "done":
          if (!waiting) setPlannedNext(false);
          return;
        case "markDone": {
          const planned = detail.procedures.find(
            (procedure) => procedure.id === asked.procedureId && procedure.status === "PLANNED",
          );
          if (planned !== undefined) markDone(planned.id);
          return;
        }
        case "removed":
          if (detail.state !== "MISSING" && !waiting) markRemoved();
          return;
        case "normal":
          if (detail.state === "MISSING") markNormal();
          return;
      }
    },
    onAsked,
  );

  // Cancelled procedures are history rather than treatment — see the note in
  // the list below on why they are still rendered at all.
  const newestFirst =
    detail === null
      ? []
      : [...detail.procedures].sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt));
  /** Not come in yet (BT6): nothing can be charted on it, and the API refuses if asked. */
  const waitingFor = primary?.waitingBehind ?? null;

  /**
   * **Both lists page to the height the band gives them** (PF13) — never a
   * scroll bar, the rule the patient file's other tabs follow (PF4). The band
   * is two rows tall on a laptop, so a tooth with a long history turns pages
   * rather than pushing the chart above it smaller.
   *
   * Recording goes back to the first page, which is where the new row is.
   */
  const recordedPages = useFitPages({
    keys: newestFirst.map((procedure) => procedure.id),
    estimate: () => ROW_ESTIMATE,
    gap: 0,
    resetOn: `${String(fdiNumber)}:${String(newestFirst.length)}`,
  });

  /** The middle column, in order: the baby-tooth box, then planned, then booked. */
  const asideKeys =
    detail === null
      ? []
      : [
          ...(waitingFor !== null && primary !== undefined ? ["waiting"] : []),
          ...(replaced !== null && primary !== undefined ? ["replaced"] : []),
          ...detail.planned.map((step) => `planned:${step.planItemId}`),
          ...detail.booked.map((booking) => `booked:${booking.appointmentId}`),
        ];
  const asidePages = useFitPages({
    keys: asideKeys,
    estimate: (index) =>
      asideKeys[index] === "waiting" || asideKeys[index] === "replaced"
        ? BOX_ESTIMATE
        : ROW_ESTIMATE,
    gap: 0,
    resetOn: `${String(fdiNumber)}:${String(asideKeys.length)}`,
  });

  if (error !== null && detail === null) {
    return (
      <div className={editorClass} data-testid="tooth-editor">
        <p className={styles.note}>{error}</p>
      </div>
    );
  }

  if (detail === null) {
    return (
      <div className={editorClass} data-testid="tooth-editor">
        <p className={styles.note}>Opening tooth {fdiNumber}…</p>
      </div>
    );
  }

  const live = detail.procedures.filter((procedure) => procedure.status !== "CANCELLED");

  /** Parts completed work has touched — blue in the picker, as on the chart. */
  const worked = SURFACE_ORDER.filter((surface) =>
    live.some(
      (procedure) => procedure.status === "COMPLETED" && procedure.surfaces.includes(surface),
    ),
  );
  /** Parts planned work names — red in the picker, as on the chart. */
  const plannedParts = SURFACE_ORDER.filter((surface) =>
    live.some(
      (procedure) => procedure.status === "PLANNED" && procedure.surfaces.includes(surface),
    ),
  );
  /** What the next procedure is recorded with: the chosen parts, M-O-D-B-L order. */
  const surfacesToSend = SURFACE_ORDER.filter((surface) => chosen.includes(surface));
  const toggleSurface = (surface: Surface): void =>
    setChosen((was) =>
      was.includes(surface) ? was.filter((s) => s !== surface) : [...was, surface],
    );

  /**
   * **Which part of the tooth** (optional), beside whichever adder is showing —
   * beside rather than above, because the band is two list rows tall on a
   * laptop and the list must keep them. Pick the parts first, then the
   * procedure: choosing the procedure records it at once, as it always has,
   * now carrying the parts with it.
   */
  const withSurfacePicker = (adder: React.ReactNode): React.JSX.Element => (
    <div className={styles.surfacePick} data-testid="surface-pick">
      <SurfaceSquare
        fdiNumber={detail.fdiNumber}
        worked={worked}
        planned={plannedParts}
        chosen={chosen}
        onToggle={toggleSurface}
        disabled={busy}
        className={styles.surfaceSquare}
      />
      <div className={styles.surfaceText}>
        <div className={styles.surfaceLine}>
          <span className={styles.recordAs} role="group" aria-label="Record as">
            <button
              type="button"
              className={styles.whenOption}
              aria-pressed={!plannedNext}
              disabled={busy}
              onClick={() => setPlannedNext(false)}
              data-testid="record-done"
            >
              Done now
            </button>
            <button
              type="button"
              className={styles.whenOption}
              aria-pressed={plannedNext}
              disabled={busy}
              onClick={() => setPlannedNext(true)}
              data-testid="record-planned"
            >
              To be done
            </button>
          </span>
          <span className={styles.surfaceChosen} data-testid="surface-chosen">
            {surfacesToSend.length === 0
              ? "Whole tooth · tap a part"
              : surfacesToSend.map((surface) => surfaceName(surface, detail.fdiNumber)).join(", ")}
          </span>
          {surfacesToSend.length > 0 && (
            <button
              type="button"
              className={styles.surfaceClear}
              disabled={busy}
              onClick={() => setChosen([])}
              data-testid="surface-clear"
            >
              Clear
            </button>
          )}
        </div>
        {adder}
      </div>
    </div>
  );

  /**
   * The catalogue, plus one row that opens the free-text field.
   *
   * Typing stays always available, because a branch catalogue ships empty and a
   * clinic's first day cannot be un-chartable (STORY-068). It is a choice inside
   * the list now rather than a second field beside it.
   */
  const options = [
    ...catalogue.map((code) => ({ value: code.id, label: code.label })),
    { value: OTHER, label: "Something else — type it", keywords: "free text other custom" },
  ];

  const isBaby = isPrimaryTooth(detail.fdiNumber);

  const [recordedFrom, recordedTo] = recordedPages.range;
  const [asideFrom, asideTo] = asidePages.range;
  const asideShown = new Set(asideKeys.slice(asideFrom, asideTo));
  const plannedShown = detail.planned.filter((step) =>
    asideShown.has(`planned:${step.planItemId}`),
  );
  const bookedShown = detail.booked.filter((booking) =>
    asideShown.has(`booked:${booking.appointmentId}`),
  );

  return (
    <div className={editorClass} data-testid="tooth-editor" data-tooth={detail.fdiNumber}>
      <header className={styles.head}>
        <div className={styles.headMain}>
          <div className={styles.label}>Tooth {detail.fdiNumber}</div>
          <h3 className={styles.name}>{detail.name}</h3>
        </div>
        <div className={styles.headAside}>
          <div className={styles.state} data-testid="tooth-state" data-state={detail.state}>
            {/* A baby tooth that is gone has fallen (BT7) — the same MISSING state. */}
            {detail.state === "MISSING" && isBaby
              ? "Fallen / removed"
              : (STATE_LABEL[detail.state] ?? detail.state)}
          </div>
          {onClose !== undefined && (
            <button
              type="button"
              className={styles.close}
              onClick={onClose}
              aria-label={`Close tooth ${String(detail.fdiNumber)}`}
              data-testid="tooth-editor-close"
            >
              ×
            </button>
          )}
        </div>
      </header>

      {error !== null && (
        <p className={styles.note} data-testid="tooth-error">
          {error}
        </p>
      )}

      <div className={`${styles.section} ${styles.onTooth}`}>
        <div className={styles.sectionTitle}>
          On this tooth
          {live.length > 0 && (
            <span className={styles.count} data-testid="tooth-count">
              {live.length}
            </span>
          )}
          <span className={styles.pagerSlot}>
            <Pager
              compact
              page={recordedPages.page}
              count={recordedPages.pages.length}
              onPage={recordedPages.setPage}
              from={recordedFrom + 1}
              to={recordedTo}
              total={newestFirst.length}
              noun="procedures"
              testId="tooth-recorded-pager"
            />
          </span>
        </div>

        {detail.procedures.length === 0 ? (
          <p className={styles.empty} data-testid="tooth-no-work">
            Nothing recorded yet.
          </p>
        ) : (
          <div className={styles.pageArea} ref={recordedPages.areaRef}>
            <ul className={styles.recorded} data-testid="tooth-recorded">
              {newestFirst.slice(recordedFrom, recordedTo).map((procedure) => (
                <li
                  key={procedure.id}
                  ref={recordedPages.measure(procedure.id)}
                  className={styles.recordedItem}
                  data-status={procedure.status}
                  data-testid="tooth-procedure"
                >
                  {/*
                    Planned work says so where the date would be (TS-D1): it has
                    not been done, so it has no date of its own to show, and the
                    word costs the row no width beside *Mark done*.
                  */}
                  {procedure.status === "PLANNED" ? (
                    <span className={`${styles.when} ${styles.toDo}`}>Planned</span>
                  ) : (
                    <span className={styles.when}>
                      {new Date(procedure.createdAt).toLocaleDateString(undefined, {
                        day: "numeric",
                        month: "short",
                        timeZone: timezone,
                      })}
                    </span>
                  )}
                  <span className={styles.what}>
                    {procedure.label}
                    {procedure.surfaces.length > 0 && (
                      <span
                        className={styles.surfaces}
                        title={procedure.surfaces
                          .map((surface) => surfaceName(surface as Surface, detail.fdiNumber))
                          .join(", ")}
                      >
                        {" "}
                        ·{" "}
                        {procedure.surfaces
                          .map((surface) => surfaceLetter(surface as Surface, detail.fdiNumber))
                          .join("")}
                      </span>
                    )}
                    {/*
                    Written before T4 (2026-08-31), when removing a procedure
                    cancelled the row rather than deleting it. Nothing this
                    screen creates can reach this state any more, but charts
                    written before that date carry rows cancelled the old way and
                    they have to keep reading as removed rather than as work the
                    patient had done.
                  */}
                    {procedure.status === "CANCELLED" && (
                      <span className={styles.removed}> — removed</span>
                    )}
                  </span>
                  {charts && procedure.status === "PLANNED" && (
                    <button
                      type="button"
                      className={styles.markDone}
                      disabled={busy}
                      aria-label={`Mark ${procedure.label} done`}
                      title="Mark done"
                      data-testid="complete-procedure"
                      onClick={() => markDone(procedure.id)}
                    >
                      ✓
                    </button>
                  )}
                  {charts && procedure.status !== "CANCELLED" && (
                    <button
                      type="button"
                      className={styles.remove}
                      disabled={busy}
                      // "Remove" in the interface, cancel in the record. The row is
                      // never destroyed — see the API's own comment on why.
                      aria-label={`Remove ${procedure.label}`}
                      data-testid="remove-procedure"
                      onClick={() =>
                        apply(() => cancelToothProcedure(patientId, fdiNumber, procedure.id))
                      }
                    >
                      ×
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

        {!charts || waitingFor !== null ? null : typing ? (
          <form
            className={styles.freeText}
            onSubmit={(event) => {
              event.preventDefault();
              const trimmed = label.trim();
              if (trimmed === "" || busy) return;
              apply(() =>
                recordToothProcedure(patientId, fdiNumber, {
                  label: trimmed,
                  ...(surfacesToSend.length > 0 ? { surfaces: surfacesToSend } : {}),
                  ...(plannedNext ? { status: "PLANNED" as const } : {}),
                }),
              );
            }}
          >
            {withSurfacePicker(
              <>
                <input
                  className={styles.input}
                  value={label}
                  onChange={(event) => setLabel(event.target.value)}
                  placeholder="What was done"
                  aria-label="Procedure"
                  data-testid="procedure-label"
                  disabled={busy}
                />
                <div className={styles.freeTextActions}>
                  <button
                    type="submit"
                    className={styles.add}
                    disabled={busy || label.trim() === ""}
                    data-testid="add-procedure"
                  >
                    Add
                  </button>
                  <button
                    type="button"
                    className={styles.cancelTyping}
                    disabled={busy}
                    data-testid="cancel-typing"
                    onClick={() => {
                      setTyping(false);
                      setLabel("");
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </>,
            )}
          </form>
        ) : (
          <div className={styles.adder}>
            {withSurfacePicker(
              <Select
                id="tooth-add"
                label="Record a procedure"
                value=""
                options={options}
                placeholder="Choose or search…"
                testId="catalogue-select"
                disabled={busy}
                onChange={(next) => {
                  if (next === OTHER) {
                    setTyping(true);
                    return;
                  }
                  const picked = catalogue.find((code) => code.id === next);
                  if (picked === undefined) return;
                  apply(() =>
                    recordToothProcedure(patientId, fdiNumber, {
                      label: picked.label,
                      procedureCodeId: picked.id,
                      ...(surfacesToSend.length > 0 ? { surfaces: surfacesToSend } : {}),
                      ...(plannedNext ? { status: "PLANNED" as const } : {}),
                    }),
                  );
                }}
              />,
            )}
          </div>
        )}
      </div>

      {/*
        **What a plan intends for this tooth** (decision D7).

        Read from the plan rather than charted: these are not tooth procedures
        and deliberately do not become them until the work is done, so there is
        nothing to tick here and the link goes to the plan, which is the thing
        that can actually be changed. Drawn only when there is something
        planned, which for most teeth there never is.
      */}
      {/*
        Planned and booked share the middle column when the editor is wide;
        otherwise this wrapper draws nothing of its own.
      */}
      <div className={styles.aside}>
        {/*
          **One heading and one pager for the column** (PF13). The baby-tooth
          box, then what is planned, then what is booked, paged together to the
          band's height; each row still says which it is.
        */}
        <div className={styles.sectionTitle}>
          Planned and booked
          <span className={styles.pagerSlot}>
            <Pager
              compact
              page={asidePages.page}
              count={asidePages.pages.length}
              onPage={asidePages.setPage}
              from={asideFrom + 1}
              to={asideTo}
              total={asideKeys.length}
              noun="planned and booked items"
              testId="tooth-aside-pager"
            />
          </span>
        </div>

        {asideKeys.length === 0 ? (
          <p className={styles.empty} data-testid="tooth-nothing-planned">
            Nothing planned or booked.
          </p>
        ) : (
          <div className={styles.pageArea} ref={asidePages.areaRef}>
            {/*
              **An adult tooth and the baby tooth in its place** (BT10). Waiting,
              it says so and offers to bring it in, which is what a double-click
              on the chart does. Come in, it says which baby tooth it replaced,
              shows what was recorded on it, and offers the way back.
            */}
            {asideShown.has("waiting") && waitingFor !== null && primary !== undefined && (
              <div
                className={styles.babyBox}
                ref={asidePages.measure("waiting")}
                data-testid="tooth-waiting"
              >
                <div className={styles.sectionTitle}>Not come in yet</div>
                <p className={styles.note}>
                  Baby tooth {waitingFor} is still there. When this tooth has come in, it takes{" "}
                  {waitingFor}&rsquo;s place. Double-clicking it on the chart does the same.
                </p>
                {charts && (
                  <button
                    type="button"
                    className={styles.boxAction}
                    disabled={busy}
                    onClick={() => primary.onReplace(fdiNumber)}
                    data-testid="replace-primary"
                  >
                    It has come in: replace {waitingFor}
                  </button>
                )}
              </div>
            )}

            {asideShown.has("replaced") && replaced !== null && primary !== undefined && (
              <div
                className={styles.babyBox}
                ref={asidePages.measure("replaced")}
                data-testid="tooth-replaced"
              >
                <div className={styles.sectionTitle}>Came in for baby tooth {replaced}</div>
                <p className={styles.note}>
                  Baby tooth {replaced} is off the chart now. What was recorded on it stays in
                  History.
                </p>
                {babyDetail !== null &&
                  babyDetail.procedures.some((procedure) => procedure.status !== "CANCELLED") && (
                    <ul className={styles.recorded}>
                      {babyDetail.procedures
                        .filter((procedure) => procedure.status !== "CANCELLED")
                        .map((procedure) => (
                          <li key={procedure.id} className={styles.recordedItem}>
                            <span className={styles.when}>
                              {new Date(procedure.createdAt).toLocaleDateString(undefined, {
                                day: "numeric",
                                month: "short",
                                timeZone: timezone,
                              })}
                            </span>
                            <span className={styles.what}>{procedure.label}</span>
                          </li>
                        ))}
                    </ul>
                  )}
                {charts && (
                  <button
                    type="button"
                    className={styles.boxAction}
                    disabled={busy}
                    onClick={() => primary.onPutBack(fdiNumber)}
                    data-testid="put-back-primary"
                  >
                    Put baby tooth {replaced} back
                  </button>
                )}
              </div>
            )}

            {/*
              **What a plan intends for this tooth** (decision D7). Read from the
              plan rather than charted: these are not tooth procedures and
              deliberately do not become them until the work is done, so there is
              nothing to tick here and the link goes to the plan.
            */}
            {plannedShown.length > 0 && (
              <ul className={styles.recorded} data-testid="tooth-planned">
                {plannedShown.map((step) => (
                  <li
                    key={step.planItemId}
                    ref={asidePages.measure(`planned:${step.planItemId}`)}
                    className={styles.recordedItem}
                    data-status={step.status}
                  >
                    <span className={styles.when}>Planned · Visit {step.visitNumber}</span>
                    <span className={styles.what}>
                      {step.label ?? "Planned work"}
                      {step.status === "SCHEDULED" && " — booked"}
                      <Link to={`/plans/${step.planId}`} className={styles.planLink}>
                        {step.planTitle ?? "the plan"}
                      </Link>
                    </span>
                  </li>
                ))}
              </ul>
            )}

            {/*
              **Bookings on this tooth still to happen** (BF3) — what makes it red
              on the chart, said here: when, and for what. The date opens that day
              on the calendar.
            */}
            {bookedShown.length > 0 && (
              <ul className={styles.recorded} data-testid="tooth-booked">
                {bookedShown.map((booking) => (
                  <li
                    key={booking.appointmentId}
                    ref={asidePages.measure(`booked:${booking.appointmentId}`)}
                    className={styles.recordedItem}
                  >
                    <span className={styles.when}>
                      <Link
                        to={`/calendar?appointment=${booking.appointmentId}`}
                        className={styles.planLink}
                      >
                        {whenOf(booking.startsAt, timezone)}
                      </Link>
                    </span>
                    <span className={styles.what}>{booking.label ?? "Booked work"}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      {/*
        The two whole-tooth states, at the foot and deliberately quiet: they are
        rare, and one of them is close to irreversible.
      */}
      {charts && (
        <div className={styles.actions}>
          <button
            type="button"
            className={styles.action}
            disabled={busy || detail.state === "MISSING" || waitingFor !== null}
            data-testid="mark-removed"
            onClick={markRemoved}
          >
            {isBaby ? "Mark fallen" : "Mark removed"}
          </button>
          {/*
          **Only on a tooth marked removed or fallen** (BT11). The server
          re-derives the tooth from its record, which changes nothing unless
          the mark is what it is undoing, so the button used to look pressable
          and do nothing on every other tooth.
        */}
          <button
            type="button"
            className={styles.action}
            disabled={busy || detail.state !== "MISSING"}
            data-testid="mark-normal"
            onClick={markNormal}
          >
            Return to normal
          </button>
          {detail.state !== "MISSING" && (
            <p className={styles.why} data-testid="mark-normal-why">
              Return to normal undoes {isBaby ? "Mark fallen" : "Mark removed"}.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
